/** Ticket-list loading shared by the Board and List views. */

import { useCallback, useEffect, useState } from 'react'
import { api, request } from '../api.js'
import { filtersToParams } from './filters.js'

const CLOSED = new Set(['done', 'wont_do'])

/**
 * Load tickets for the current filters, plus the blocker index.
 *
 * The blocker index comes from `GET /api/v1/graph`, where a `blocks` edge runs
 * source → target; a ticket counts as blocked when at least one ticket blocking
 * it is not yet closed. `GET /tickets` alone cannot answer that, since its rows
 * carry no links.
 *
 * @param {object} filters Filter state from `useFilters`.
 * @returns {{tickets: object[], total: number, blockers: Map<number, string[]>,
 *            loading: boolean, error: Error|null, reload: Function,
 *            patchLocal: Function}}
 */
export function useTickets(filters) {
  const [state, setState] = useState({ tickets: [], total: 0, loading: true, error: null })
  const [blockers, setBlockers] = useState(new Map())
  const [nonce, setNonce] = useState(0)

  const reload = useCallback(() => setNonce((n) => n + 1), [])

  useEffect(() => {
    const controller = new AbortController()
    setState((s) => ({ ...s, loading: true, error: null }))
    api
      .tickets(filtersToParams(filters), controller.signal)
      .then((page) =>
        setState({ tickets: page.items, total: page.total, loading: false, error: null }),
      )
      .catch((error) => {
        if (error.name === 'AbortError') return
        setState({ tickets: [], total: 0, loading: false, error })
      })
    return () => controller.abort()
  }, [
    filters.q,
    filters.sort,
    filters.city.join(','),
    filters.component.join(','),
    filters.assignee.join(','),
    filters.kind.join(','),
    filters.priority.join(','),
    filters.status.join(','),
    nonce,
  ])

  useEffect(() => {
    const controller = new AbortController()
    request('/graph', { signal: controller.signal })
      .then((graph) => {
        const byId = new Map(graph.nodes.map((n) => [n.id, n]))
        const index = new Map()
        for (const edge of graph.edges) {
          if (edge.type !== 'blocks') continue
          const source = byId.get(edge.source)
          if (source && CLOSED.has(source.status)) continue
          const list = index.get(edge.target) || []
          list.push(source ? source.key : String(edge.source))
          index.set(edge.target, list)
        }
        setBlockers(index)
      })
      .catch(() => setBlockers(new Map()))
    return () => controller.abort()
  }, [nonce])

  /** Apply a local field change to one ticket without a round trip. */
  const patchLocal = useCallback((id, patch) => {
    setState((s) => ({
      ...s,
      tickets: s.tickets.map((t) => (t.id === id ? { ...t, ...patch } : t)),
    }))
  }, [])

  return { ...state, blockers, reload, patchLocal }
}
